import React, {FC, Fragment, ReactNode, RefObject} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {Dropdown, DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {InboxesSearchQueryBuilderAsyncDropdownInput} from './inboxesSearchQueryBuilderAsyncDropdownInput';
import {InboxesSearchQueryBuilderRecipientInput} from './inboxesSearchQueryBuilderRecipientInput';
import {InboxesSearchQueryBuilderSyncDropdownInput} from './inboxesSearchQueryBuilderSyncDropdownInput';
import {InboxesSearchQueryBuilderTextInput} from './inboxesSearchQueryBuilderTextInput';
import {
  InboxesSearchQueryBuilderInputConfig,
  InputValue,
  isAsyncDropdownInputConfig,
  isRecipientInputConfig,
  isSyncDropdownInputConfig,
} from './inboxesSearchQueryBuilderTypes';
import {QueryBuilderForm} from './useInboxesSearchQueryBuilderForm';

/*
 * Props.
 */

interface InboxesSearchQueryBuilderDropdownProps extends DropdownProps {
  innerRef: RefObject<HTMLDivElement>;
  searchQuery?: string;
  queryHash?: string;
  queryBuilderForm: QueryBuilderForm;
  onClose: HandlerOf<boolean>;
  onSearchClick: HandlerOf<string>;
}

/*
 * Style.
 */

const StyledDropdownInnerWrapperDiv = styled.div`
  padding: 20px 16px;

  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  grid-column-gap: 16px;
  grid-row-gap: 20px;

  align-items: center;
`;

const StyledLabelSpan = styled.span`
  max-width: 150px;

  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};

  ${ellipsis()}
`;

const StyledButtonGroup = styled(ButtonGroup)`
  padding: 0 16px 20px;

  display: flex;
  justify-content: flex-end;
`;

/*
 * Component.
 */

export const InboxesSearchQueryBuilderDropdown: FC<InboxesSearchQueryBuilderDropdownProps> = (props) => {
  const {innerRef, searchQuery, queryHash, onClose, onSearchClick, queryBuilderForm, ...dropdownProps} =
    props;

  const {inputsConfig, values, handleChange, handleReset, handleSubmit, areFormButtonsDisabled} =
    queryBuilderForm;

  const onSearch = () => {
    const newSearchQuery = handleSubmit();

    if (newSearchQuery !== searchQuery) {
      onSearchClick(newSearchQuery);

      // Close the dropdown - and skip refocus.
      onClose(true);
    }
  };

  const renderInputConfig = (input: InboxesSearchQueryBuilderInputConfig) => (
    <Fragment key={input.id}>
      <StyledLabelSpan>{input.label}</StyledLabelSpan>
      {renderInput(input, values[input.id], handleChange(input.id), onSearch)}
    </Fragment>
  );

  return (
    <Dropdown {...dropdownProps} onRequestClose={() => onClose(false)}>
      <div role="presentation" ref={innerRef} onDoubleClick={(e) => e.preventDefault()}>
        <StyledDropdownInnerWrapperDiv>{inputsConfig.map(renderInputConfig)}</StyledDropdownInnerWrapperDiv>
        <StyledButtonGroup>
          <Button
            onClick={() => handleReset()}
            isDisabled={areFormButtonsDisabled}
            interactionId="search-query-builder-reset-button"
          >
            <Trans id="search-query-builder-reset">Reset</Trans>
          </Button>
          <Button
            onClick={() => onSearch()}
            isDisabled={areFormButtonsDisabled}
            variant={ButtonVariantsEnum.PRIMARY}
          >
            <Trans id="search-query-builder-search">Search</Trans>
          </Button>
        </StyledButtonGroup>
      </div>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function renderInput(
  inputConfig: InboxesSearchQueryBuilderInputConfig,
  value: InputValue,
  onChange: HandlerOf<InputValue>,
  onSearch: Handler,
): ReactNode {
  if (isRecipientInputConfig(inputConfig)) {
    return (
      <InboxesSearchQueryBuilderRecipientInput
        {...inputConfig}
        inputValue={value}
        onChange={onChange}
        onSearch={onSearch}
      />
    );
  }

  if (isSyncDropdownInputConfig(inputConfig)) {
    return (
      <InboxesSearchQueryBuilderSyncDropdownInput {...inputConfig} inputValue={value} onChange={onChange} />
    );
  }

  if (isAsyncDropdownInputConfig(inputConfig)) {
    return (
      <InboxesSearchQueryBuilderAsyncDropdownInput {...inputConfig} inputValue={value} onChange={onChange} />
    );
  }

  return (
    <InboxesSearchQueryBuilderTextInput
      {...inputConfig}
      inputValue={value}
      onChange={onChange}
      onSearch={onSearch}
    />
  );
}
